// react
import { useContext, useState } from "react";
// libs
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { useToggle } from "react-use";
// components
import { SceneCursor } from "../SceneCursor";
import SceneFocus from "./SceneFocus";
import { SceneMarkerList } from "./SceneMarkerList";
import { SceneBackground } from "./SceneBackground";
import { SceneMenu } from "./SceneMenu";
import { CircularMotion } from "../CircularMotion";
import SceneGuide from "./SceneGuide";
import { CursorHolder } from "../CursorHolder";
// context
import { ShowIntroContext } from "../../context/IntroContext";
// other
import { SceneItem } from "../../models/client";
import { fill } from "../../styles/utils";

interface SceneProps {
  isAnimating: boolean;
}

const Wrapper = styled.div`
  ${fill()}
  box-shadow: 50px 20px 100px 0 rgba(0, 0, 0, 0.41);
  z-index: 0;

  &::after {
    content: "";
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 40%;
    background: linear-gradient(180deg, #02041f 0%, rgba(2, 3, 37, 0) 100%);
  }
`;

const Window = styled.div`
  ${fill()}
  overflow: hidden;
`;

function Scene({ isAnimating }: SceneProps) {
  const { showIntro, setShowIntro } = useContext(ShowIntroContext);
  const [expandCursor, setExpandCursor] = useToggle(false);
  const [sceneMouseOver, setSceneMouseOver] = useToggle(false);
  const [markerMouseOver, setMarkerMouseOver] = useToggle(false);
  const [showGuide, setShowGuide] = useToggle(true);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const [selected, setSelected] = useState<SceneItem | null>(null);

  const handleFocusCloseTrigger = () => {
    setSelected(null);
  };

  const handleMouseOver: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!sceneMouseOver) {
      if (!isMobile) {
        setSceneMouseOver(true);
      }
    }
  };

  const handleMarkerMouseOver = () => {
    if (!markerMouseOver) {
      setMarkerMouseOver(true);
    }
  };

  const handleCloseGuideClick = () => {
    setShowGuide(false);
    setShowIntro(false);
  };

  const handleSceneMenuToggle = () => {
    if (showGuide || showIntro) {
      setShowGuide(false);
      setShowIntro(false);
    }
  };

  return (
    <>
      {!isMobile && (
        <CursorHolder>
          <SceneCursor
            isAnimating={true}
            expanded={expandCursor}
            hidden={!(sceneMouseOver && !markerMouseOver)}
          />
        </CursorHolder>
      )}
      <Wrapper>
        <Window
          onMouseOver={handleMouseOver}
          onMouseLeave={() => setSceneMouseOver(false)}
          onMouseDown={() => setExpandCursor(true)}
          onMouseUp={() => setExpandCursor(false)}
        >
          <SceneBackground
            zoomOut={showGuide && showIntro}
            radius={16}
            isAnimating={isAnimating}
          >
            <CircularMotion radius={16} duration={16} isAnimating={isAnimating}>
              <SceneMarkerList
                onMarkerClick={setSelected}
                onMarkerMouseEnter={handleMarkerMouseOver}
                onMarkerMouseLeave={() => setMarkerMouseOver(false)}
              />
            </CircularMotion>
          </SceneBackground>
        </Window>

        <SceneGuide
          showGuide={showGuide && showIntro}
          onCloseClick={handleCloseGuideClick}
        />

        <SceneMenu
          guideShown={showGuide}
          onSceneMenuToggle={handleSceneMenuToggle}
        />
      </Wrapper>

      <AnimatePresence>
        {selected && (
          <SceneFocus
            image={selected.image}
            slug={selected.slug}
            title={selected.title}
            onCloseTrigger={handleFocusCloseTrigger}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default Scene;
